import { useState, useEffect, useCallback } from "react";
import { listDrafts, deleteDraft, formatDraftStamp, draftWho } from "../drafts";
import { can } from "../config";

// Everything in the unit that someone has started and not submitted.
//
// A draft is shared: whoever is free can open it and finish it, which is the point on a
// shop floor where the person who opened a breakdown is often not the person who closes
// it. Nothing here is a maintenance record — these entries are in their own collection
// and no report reads them until they are submitted.
export default function DraftsPage({ user, unitId, units = [], machines = [], onOpenMachine }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setDrafts(await listDrafts(unitId));
    setLoading(false);
  }, [unitId]);

  useEffect(() => { load(); }, [load]);

  const unitName = id => units.find(u => u.id === id)?.shortName || id;
  const machineFor = d => machines.find(m => m.id === d.machineId);

  async function discard(d) {
    if (!window.confirm(`Discard the draft for ${d.machineCode || "this machine"}? It will be removed for everyone.`)) return;
    setBusyId(d.id);
    await deleteDraft(d.unitId, d.machineId);
    await load();
    setBusyId("");
  }

  return (
    <div className="space-y-4" data-testid="drafts-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-bold text-gray-800">Drafts</h2>
          <p className="text-xs text-gray-500">
            Maintenance logs someone has started but not submitted. Anyone can open one and finish it.
            Nothing here counts in Reports until it is submitted.
          </p>
        </div>
        <button onClick={load} className="text-xs border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50"
          data-testid="drafts-refresh">Refresh</button>
      </div>

      {loading ? (
        <div className="bg-white border rounded-xl p-6 text-sm text-gray-500">Loading…</div>
      ) : drafts.length === 0 ? (
        <div className="bg-white border rounded-xl p-10 text-center" data-testid="drafts-empty">
          <p className="text-gray-500 font-medium">Nothing in progress</p>
          <p className="text-xs text-gray-400 mt-1">
            A maintenance log you start and leave unfinished will appear here for the whole unit.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                {["Machine", "Work", "Started by / last edited", "Saved", ""].map(h => (
                  <th key={h} className="text-left px-4 py-2 text-xs text-gray-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {drafts.map(d => {
                const m = machineFor(d);
                const form = d.form || {};
                const external = form.workType === "external";
                return (
                  <tr key={d.id} className="border-b hover:bg-gray-50" data-testid={`draft-row-${d.machineId}`}>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => m && onOpenMachine?.(m)}
                        disabled={!m}
                        className={`font-medium text-left ${m ? "text-blue-600 hover:underline" : "text-gray-500 cursor-default"}`}
                        data-testid={`draft-open-${d.machineId}`}
                        title={m ? "Open this machine to continue the draft" : "This machine is not in the current unit"}
                      >
                        {d.machineName || m?.name || "—"}
                      </button>
                      <div className="text-xs font-mono text-gray-400">
                        {d.machineCode || m?.machineNo || "—"}
                        {d.unitId !== unitId && <span className="ml-2 text-purple-600">{unitName(d.unitId)}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${external ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>
                        {external ? "External" : "Internal"}
                      </span>
                      <div className="text-xs text-gray-500 mt-1 max-w-xs truncate" title={form.description || ""}>
                        {form.description || <span className="text-gray-400">No description yet</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{draftWho(d)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDraftStamp(d.updatedAt)}</td>
                    <td className="px-4 py-3 text-right">
                      {can(user, "addHistory") && (
                        <button onClick={() => discard(d)} disabled={busyId === d.id}
                          className="text-xs text-red-400 hover:text-red-600 disabled:opacity-50"
                          data-testid={`draft-discard-${d.machineId}`}>
                          {busyId === d.id ? "Discarding…" : "Discard"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
